import {
    cinemaDayEndInZone,
    cinemaDayKeyOf,
    cinemaDayStartInZone,
    DEFAULT_TIME_ZONE,
    isDayKey,
    zonedDayKey,
    zonedInstant,
    type DayKey
} from '@cineamo/ui/src/helpers/cinema-time-zone';
import { formatDate_yyyyMMddTHHmmssZ } from '@cineamo/ui/src/helpers/date-helper';

/**
 * Which day a showing belongs to, across every guest-facing surface that groups showings by day —
 * the showtimes finder, the movie landing pages, the program page.
 *
 * These list German, Austrian and Swiss cinemas, and a screening belongs to the day the cinema
 * programmed it under — never to the day it happens to be where the guest is sitting. So every date
 * here is reckoned on one clock, `Europe/Berlin`, whatever the browser's zone says.
 *
 * It used to be reckoned with `Date#setHours`, which reads and writes the *browser's* zone. That
 * looked self-consistent — the same wrong zone in the day buttons, the counts and the query window —
 * and was wrong for everyone outside CET: a guest in New York opening the finder got day buttons
 * labeled one day early, and the "today" button fetched 04:00 New York to 03:59 New York, a window
 * running to 09:59 Berlin that dropped that evening's screenings and picked up the previous night's.
 * Anyone east of Berlin got the mirror image. The program page was worse again: its counts keyed on
 * the UTC date (`startDatetime.substring(0, 10)`) while its filter compared browser-local days, so a
 * 01:00 screening was counted on one box and listed under the next — in Berlin too.
 *
 * A day is carried as a **day key** (`YYYY-MM-DD`) rather than a `Date`, because a key names the
 * same day in every zone and a `Date` does not. It is also what `selectedDate` already is on the
 * wire, so the URL parameter, the query keys and the day buttons all speak one language.
 *
 * The 04:00 boundary itself lives in `cinema-day.ts`; the zone arithmetic in `cinema-time-zone.ts`.
 * This module only fixes the zone those are applied in.
 */
export const SHOWTIMES_TIME_ZONE = DEFAULT_TIME_ZONE;

/** The Cinema Day that is running right now in Berlin — before 04:00 there, still yesterday's. */
export function todayShowtimesDayKey(): DayKey {
    return cinemaDayKeyOf(new Date(), SHOWTIMES_TIME_ZONE);
}

/** The Cinema Day a showing's `startDatetime` falls inside, in Berlin. */
export function showtimesDayKeyOf(instant: Date | string): DayKey {
    return cinemaDayKeyOf(new Date(instant), SHOWTIMES_TIME_ZONE);
}

/**
 * The day key of an API date field that *names a calendar day* rather than an instant — a movie's
 * release date, above all.
 *
 * Such a field arrives either as a bare `YYYY-MM-DD` or, from TMDB, as that day at UTC midnight
 * (`2026-10-01T00:00:00.000Z`). Both mean the same calendar day, so the leading date is taken
 * verbatim instead of being parsed into an instant: `new Date('2026-10-01')` is UTC midnight, which
 * is still Sept 30 for every zone west of Greenwich and lands one day early there.
 */
export function showtimesCalendarDayKey(value: string): DayKey {
    const leadingDate = value.slice(0, 10);
    return isDayKey(leadingDate) ? leadingDate : zonedDayKey(new Date(value), SHOWTIMES_TIME_ZONE);
}

/** 04:00 Berlin on `dayKey` — the instant that Cinema Day begins. */
export function showtimesDayStart(dayKey: DayKey): Date {
    return cinemaDayStartInZone(dayKey, SHOWTIMES_TIME_ZONE);
}

/** 03:59:59.999 Berlin the following morning — the last instant of the Cinema Day `dayKey` names. */
export function showtimesDayEnd(dayKey: DayKey): Date {
    return cinemaDayEndInZone(dayKey, SHOWTIMES_TIME_ZONE);
}

/** The instant Berlin's clock reads `hour`:00 on the calendar day `dayKey` names. */
export function showtimesDayHour(dayKey: DayKey, hour: number): Date {
    return zonedInstant(dayKey, hour, SHOWTIMES_TIME_ZONE);
}

/**
 * The `startDatetime`/`endDatetime` pair to send to `/showings` for a finder selection.
 *
 * `selectedDate` is a day key for a single day, or a multi-day sentinel (`all`, `previews`) — those
 * open at the start of today's Cinema Day and are left open-ended, so a caller that knows a closing
 * bound (previews stop before the release day) adds its own `endDatetime` on top.
 *
 * `startTime`/`endTime` are the time-of-day filter's hours, read on Berlin's clock like everything
 * else. They replace the Cinema Day bounds rather than narrowing them, which is what the filter
 * means: "before 18:00" runs from the day's 04:00 start to 18:00, "after 22:00" from 22:00 to the
 * day's 03:59 end. They are typed loosely because they reach every caller out of the same
 * `UrlQueryParameter` bag as the server filters, which holds strings and booleans too — anything
 * that is not a number is simply not a time-of-day filter.
 */
export function showtimesDayWindow(
    selectedDate: string,
    { startTime, endTime }: { startTime?: unknown; endTime?: unknown } = {}
): { startDatetime: string; endDatetime?: string } {
    if (!isDayKey(selectedDate)) {
        return { startDatetime: formatDate_yyyyMMddTHHmmssZ(showtimesDayStart(todayShowtimesDayKey())) };
    }

    const start =
        typeof startTime === 'number' ? showtimesDayHour(selectedDate, startTime) : showtimesDayStart(selectedDate);
    const end = typeof endTime === 'number' ? showtimesDayHour(selectedDate, endTime) : showtimesDayEnd(selectedDate);

    return {
        startDatetime: formatDate_yyyyMMddTHHmmssZ(start),
        endDatetime: formatDate_yyyyMMddTHHmmssZ(end)
    };
}
